/**
 * Filter Bar Component
 *
 * Horizontal filter bar with:
 * - Asset class filter chips (horizontal scrollable)
 * - Category dropdown selector
 * - Plan toggle (Direct Only switch)
 * - Rating filter (star rating or health status)
 * - Sort dropdown (by score, returns, AUM, expense ratio)
 */

import * as React from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ChevronDown,
  X,
  Check,
  Star,
  ArrowUpDown,
  SlidersHorizontal,
} from "lucide-react";
import type { FundRating, FundCategory } from "@curie/shared";

// ============================================================================
// Types
// ============================================================================

/**
 * Asset class for filtering
 */
export type AssetClass = "all" | "equity" | "debt" | "hybrid";

/**
 * Sort field options
 */
export type SortField =
  | "score"
  | "returns1Y"
  | "returns3Y"
  | "aum"
  | "expenseRatio";

/**
 * Sort direction
 */
export type SortDirection = "asc" | "desc";

/**
 * Current filter state
 */
export interface FilterState {
  /** Selected asset class */
  assetClass: AssetClass;
  /** Selected categories (empty = all) */
  categories: FundCategory[];
  /** Direct plan only toggle */
  directOnly: boolean;
  /** Selected ratings (empty = all) */
  ratings: FundRating[];
  /** Current sort field */
  sortField: SortField;
  /** Sort direction */
  sortDirection: SortDirection;
}

/**
 * Props for the FilterBar component
 */
export interface FilterBarProps {
  /** Current filter state */
  filters: FilterState;
  /** Callback when filters change */
  onFiltersChange: (filters: FilterState) => void;
  /** Available categories to show in dropdown */
  availableCategories?: FundCategory[];
  /** Additional CSS classes */
  className?: string;
  /** Whether to show compact version (mobile) */
  compact?: boolean;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Asset class options
 */
const ASSET_CLASS_OPTIONS: { value: AssetClass; label: string }[] = [
  { value: "all", label: "All" },
  { value: "equity", label: "Equity" },
  { value: "debt", label: "Debt" },
  { value: "hybrid", label: "Hybrid" },
];

/**
 * Sort field options with labels
 */
const SORT_OPTIONS: { value: SortField; label: string }[] = [
  { value: "score", label: "Score" },
  { value: "returns1Y", label: "1Y Returns" },
  { value: "returns3Y", label: "3Y Returns" },
  { value: "aum", label: "AUM" },
  { value: "expenseRatio", label: "Expense Ratio" },
];

/**
 * Rating options with labels and colors
 */
const RATING_OPTIONS: {
  value: FundRating;
  label: string;
  color: string;
  bgColor: string;
}[] = [
  {
    value: "in-form",
    label: "In-Form",
    color: "text-emerald-700 dark:text-emerald-400",
    bgColor: "bg-emerald-100 dark:bg-emerald-900/30",
  },
  {
    value: "on-track",
    label: "On-Track",
    color: "text-sky-700 dark:text-sky-400",
    bgColor: "bg-sky-100 dark:bg-sky-900/30",
  },
  {
    value: "off-track",
    label: "Off-Track",
    color: "text-amber-700 dark:text-amber-400",
    bgColor: "bg-amber-100 dark:bg-amber-900/30",
  },
  {
    value: "out-of-form",
    label: "Out-of-Form",
    color: "text-rose-700 dark:text-rose-400",
    bgColor: "bg-rose-100 dark:bg-rose-900/30",
  },
];

/**
 * Category labels
 */
const CATEGORY_LABELS: Record<FundCategory, string> = {
  "equity-large-cap": "Large Cap",
  "equity-mid-cap": "Mid Cap",
  "equity-small-cap": "Small Cap",
  "equity-flexi-cap": "Flexi Cap",
  "equity-multi-cap": "Multi Cap",
  "equity-elss": "ELSS",
  "equity-sectoral": "Sectoral",
  "equity-global": "Global",
  "debt-liquid": "Liquid",
  "debt-short-term": "Short Term",
  "debt-medium-term": "Medium Term",
  "debt-long-term": "Long Term",
  "debt-gilt": "Gilt",
  "debt-corporate": "Corporate",
  "hybrid-aggressive": "Aggressive Hybrid",
  "hybrid-balanced": "Balanced Hybrid",
  "hybrid-conservative": "Conservative Hybrid",
};

/**
 * Default filter state
 */
export const DEFAULT_FILTER_STATE: FilterState = {
  assetClass: "all",
  categories: [],
  directOnly: false,
  ratings: [],
  sortField: "score",
  sortDirection: "desc",
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get categories for a specific asset class
 */
function getCategoriesForAssetClass(
  assetClass: AssetClass
): FundCategory[] {
  const allCategories = Object.keys(CATEGORY_LABELS) as FundCategory[];

  if (assetClass === "all") return allCategories;

  return allCategories.filter((cat) => cat.startsWith(assetClass));
}

/**
 * Count active filters
 */
function countActiveFilters(filters: FilterState): number {
  let count = 0;
  if (filters.assetClass !== "all") count++;
  if (filters.categories.length > 0) count++;
  if (filters.directOnly) count++;
  if (filters.ratings.length > 0) count++;
  return count;
}

// ============================================================================
// Sub-components
// ============================================================================

/**
 * Asset class filter chips
 */
function AssetClassChips({
  value,
  onChange,
}: {
  value: AssetClass;
  onChange: (value: AssetClass) => void;
}) {
  return (
    <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide">
      {ASSET_CLASS_OPTIONS.map((option) => (
        <button
          key={option.value}
          onClick={() => onChange(option.value)}
          className={cn(
            "px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors",
            "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1",
            value === option.value
              ? "bg-primary text-primary-foreground"
              : "bg-muted text-muted-foreground hover:bg-muted/80"
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Dropdown component (generic)
 */
function Dropdown<T extends string>({
  label,
  value,
  options,
  onChange,
  multiple = false,
  renderOption,
  className,
}: {
  label: string;
  value: T | T[];
  options: { value: T; label: string }[];
  onChange: (value: T | T[]) => void;
  multiple?: boolean;
  renderOption?: (option: { value: T; label: string }, selected: boolean) => React.ReactNode;
  className?: string;
}) {
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Close on outside click
  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedValues = Array.isArray(value) ? value : [value];
  const hasSelection = multiple
    ? (value as T[]).length > 0
    : value !== options[0]?.value;

  const displayText = multiple
    ? (value as T[]).length > 0
      ? `${(value as T[]).length} selected`
      : label
    : options.find((o) => o.value === value)?.label ?? label;

  const handleSelect = (optionValue: T) => {
    if (multiple) {
      const currentValues = value as T[];
      const newValues = currentValues.includes(optionValue)
        ? currentValues.filter((v) => v !== optionValue)
        : [...currentValues, optionValue];
      onChange(newValues);
    } else {
      onChange(optionValue);
      setIsOpen(false);
    }
  };

  return (
    <div ref={dropdownRef} className={cn("relative", className)}>
      <Button
        variant={hasSelection ? "secondary" : "outline"}
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="gap-1.5"
      >
        <span className="truncate max-w-[100px]">{displayText}</span>
        <ChevronDown
          className={cn(
            "h-4 w-4 shrink-0 transition-transform",
            isOpen && "rotate-180"
          )}
        />
      </Button>

      {isOpen && (
        <div className="absolute top-full left-0 z-50 mt-1 min-w-[160px] max-h-[280px] overflow-y-auto rounded-lg border bg-popover p-1 shadow-md">
          {options.map((option) => {
            const isSelected = selectedValues.includes(option.value);
            return (
              <button
                key={option.value}
                onClick={() => handleSelect(option.value)}
                className={cn(
                  "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm",
                  "hover:bg-accent hover:text-accent-foreground",
                  "focus:outline-none focus:bg-accent focus:text-accent-foreground",
                  isSelected && "bg-accent/50"
                )}
              >
                {multiple && (
                  <div
                    className={cn(
                      "h-4 w-4 rounded border flex items-center justify-center",
                      isSelected
                        ? "bg-primary border-primary"
                        : "border-input"
                    )}
                  >
                    {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                  </div>
                )}
                {renderOption ? (
                  renderOption(option, isSelected)
                ) : (
                  <span>{option.label}</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Plan toggle (Direct Only)
 */
function PlanToggle({
  value,
  onChange,
}: {
  value: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <button
      onClick={() => onChange(!value)}
      className={cn(
        "flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors",
        "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1",
        value
          ? "bg-primary text-primary-foreground"
          : "bg-muted text-muted-foreground hover:bg-muted/80"
      )}
    >
      <div
        className={cn(
          "h-4 w-4 rounded-full border-2 flex items-center justify-center transition-colors",
          value
            ? "border-primary-foreground bg-primary-foreground"
            : "border-current"
        )}
      >
        {value && <Check className="h-2.5 w-2.5 text-primary" />}
      </div>
      <span>Direct Only</span>
    </button>
  );
}

/**
 * Rating filter with star/health status indicators
 */
function RatingFilter({
  value,
  onChange,
}: {
  value: FundRating[];
  onChange: (value: FundRating[]) => void;
}) {
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleRating = (rating: FundRating) => {
    const newValue = value.includes(rating)
      ? value.filter((r) => r !== rating)
      : [...value, rating];
    onChange(newValue);
  };

  return (
    <div ref={dropdownRef} className="relative">
      <Button
        variant={value.length > 0 ? "secondary" : "outline"}
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="gap-1.5"
      >
        <Star className="h-4 w-4" />
        <span>{value.length > 0 ? `${value.length} Ratings` : "Rating"}</span>
        <ChevronDown
          className={cn(
            "h-4 w-4 shrink-0 transition-transform",
            isOpen && "rotate-180"
          )}
        />
      </Button>

      {isOpen && (
        <div className="absolute top-full left-0 z-50 mt-1 min-w-[180px] rounded-lg border bg-popover p-2 shadow-md">
          <div className="space-y-1">
            {RATING_OPTIONS.map((option) => {
              const isSelected = value.includes(option.value);
              return (
                <button
                  key={option.value}
                  onClick={() => toggleRating(option.value)}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm",
                    "hover:bg-accent hover:text-accent-foreground",
                    "focus:outline-none focus:bg-accent",
                    isSelected && "bg-accent/50"
                  )}
                >
                  <div
                    className={cn(
                      "h-4 w-4 rounded border flex items-center justify-center",
                      isSelected
                        ? "bg-primary border-primary"
                        : "border-input"
                    )}
                  >
                    {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                  </div>
                  <Badge
                    variant="secondary"
                    className={cn("border-0", option.bgColor, option.color)}
                  >
                    {option.label}
                  </Badge>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Sort dropdown with direction toggle
 */
function SortDropdown({
  field,
  direction,
  onFieldChange,
  onDirectionChange,
}: {
  field: SortField;
  direction: SortDirection;
  onFieldChange: (field: SortField) => void;
  onDirectionChange: (direction: SortDirection) => void;
}) {
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const currentLabel =
    SORT_OPTIONS.find((o) => o.value === field)?.label ?? "Sort";

  return (
    <div ref={dropdownRef} className="relative flex items-center gap-1">
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="gap-1.5"
      >
        <ArrowUpDown className="h-4 w-4" />
        <span className="hidden sm:inline">{currentLabel}</span>
        <ChevronDown
          className={cn(
            "h-4 w-4 shrink-0 transition-transform",
            isOpen && "rotate-180"
          )}
        />
      </Button>

      <Button
        variant="ghost"
        size="icon-sm"
        onClick={() => onDirectionChange(direction === "asc" ? "desc" : "asc")}
        className="shrink-0"
        title={direction === "asc" ? "Ascending" : "Descending"}
      >
        <ArrowUpDown
          className={cn(
            "h-4 w-4 transition-transform",
            direction === "asc" && "rotate-180"
          )}
        />
      </Button>

      {isOpen && (
        <div className="absolute top-full left-0 z-50 mt-1 min-w-[140px] rounded-lg border bg-popover p-1 shadow-md">
          {SORT_OPTIONS.map((option) => {
            const isSelected = field === option.value;
            return (
              <button
                key={option.value}
                onClick={() => {
                  onFieldChange(option.value);
                  setIsOpen(false);
                }}
                className={cn(
                  "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm",
                  "hover:bg-accent hover:text-accent-foreground",
                  "focus:outline-none focus:bg-accent",
                  isSelected && "bg-accent/50 font-medium"
                )}
              >
                {isSelected && <Check className="h-4 w-4" />}
                {!isSelected && <span className="w-4" />}
                <span>{option.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Clear filters button
 */
function ClearFiltersButton({
  filters,
  onClear,
}: {
  filters: FilterState;
  onClear: () => void;
}) {
  const activeCount = countActiveFilters(filters);

  if (activeCount === 0) return null;

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={onClear}
      className="gap-1.5 text-muted-foreground hover:text-foreground"
    >
      <X className="h-4 w-4" />
      <span>Clear ({activeCount})</span>
    </Button>
  );
}

/**
 * Active filter tags
 */
function ActiveFilterTags({
  filters,
  onRemove,
}: {
  filters: FilterState;
  onRemove: (key: keyof FilterState, value?: string) => void;
}) {
  const tags: { key: keyof FilterState; label: string; value?: string }[] = [];

  if (filters.assetClass !== "all") {
    const label =
      ASSET_CLASS_OPTIONS.find((o) => o.value === filters.assetClass)?.label ??
      filters.assetClass;
    tags.push({ key: "assetClass", label });
  }

  filters.categories.forEach((cat) => {
    tags.push({
      key: "categories",
      label: CATEGORY_LABELS[cat],
      value: cat,
    });
  });

  if (filters.directOnly) {
    tags.push({ key: "directOnly", label: "Direct Only" });
  }

  filters.ratings.forEach((rating) => {
    const option = RATING_OPTIONS.find((o) => o.value === rating);
    if (option) {
      tags.push({ key: "ratings", label: option.label, value: rating });
    }
  });

  if (tags.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {tags.map((tag, index) => (
        <Badge
          key={`${tag.key}-${tag.value ?? index}`}
          variant="secondary"
          className="gap-1 pr-1"
        >
          {tag.label}
          <button
            onClick={() => onRemove(tag.key, tag.value)}
            className="rounded-full hover:bg-accent p-0.5"
          >
            <X className="h-3 w-3" />
          </button>
        </Badge>
      ))}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * Filter Bar Component
 *
 * A comprehensive filter bar for mutual fund listings with:
 * - Asset class chips (All, Equity, Debt, Hybrid)
 * - Category multi-select dropdown
 * - Direct Only toggle
 * - Rating multi-select filter
 * - Sort dropdown with direction toggle
 *
 * @example
 * ```tsx
 * const [filters, setFilters] = useState(DEFAULT_FILTER_STATE);
 *
 * <FilterBar
 *   filters={filters}
 *   onFiltersChange={setFilters}
 * />
 * ```
 */
export function FilterBar({
  filters,
  onFiltersChange,
  availableCategories,
  className,
  compact = false,
}: FilterBarProps) {
  const updateFilter = <K extends keyof FilterState>(
    key: K,
    value: FilterState[K]
  ) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const clearFilters = () => {
    onFiltersChange(DEFAULT_FILTER_STATE);
  };

  const removeFilter = (key: keyof FilterState, value?: string) => {
    if (key === "assetClass") {
      updateFilter("assetClass", "all");
    } else if (key === "categories" && value) {
      updateFilter(
        "categories",
        filters.categories.filter((c) => c !== value)
      );
    } else if (key === "directOnly") {
      updateFilter("directOnly", false);
    } else if (key === "ratings" && value) {
      updateFilter(
        "ratings",
        filters.ratings.filter((r) => r !== value)
      );
    }
  };

  // Get available categories based on selected asset class
  const categoriesToShow =
    availableCategories ?? getCategoriesForAssetClass(filters.assetClass);

  const categoryOptions = categoriesToShow.map((cat) => ({
    value: cat,
    label: CATEGORY_LABELS[cat],
  }));

  if (compact) {
    // Compact mobile view - single row with filter button
    return (
      <div className={cn("space-y-2", className)}>
        <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-1">
          {/* Asset Class Chips */}
          <AssetClassChips
            value={filters.assetClass}
            onChange={(value) => updateFilter("assetClass", value)}
          />

          <div className="h-6 w-px bg-border shrink-0" />

          {/* Sort */}
          <SortDropdown
            field={filters.sortField}
            direction={filters.sortDirection}
            onFieldChange={(field) => updateFilter("sortField", field)}
            onDirectionChange={(dir) => updateFilter("sortDirection", dir)}
          />

          {/* Filter button that opens a sheet/modal */}
          <Button variant="outline" size="sm" className="gap-1.5 shrink-0">
            <SlidersHorizontal className="h-4 w-4" />
            <span>Filters</span>
            {countActiveFilters(filters) > 0 && (
              <Badge variant="default" className="h-5 w-5 p-0 text-xs">
                {countActiveFilters(filters)}
              </Badge>
            )}
          </Button>
        </div>

        {/* Active Filter Tags */}
        <ActiveFilterTags filters={filters} onRemove={removeFilter} />
      </div>
    );
  }

  // Full desktop view
  return (
    <div className={cn("space-y-3", className)}>
      {/* Main filter row */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Asset Class Chips */}
        <AssetClassChips
          value={filters.assetClass}
          onChange={(value) => {
            updateFilter("assetClass", value);
            // Clear category selection when changing asset class
            updateFilter("categories", []);
          }}
        />

        <div className="h-6 w-px bg-border" />

        {/* Category Dropdown */}
        <Dropdown<FundCategory>
          label="Category"
          value={filters.categories}
          options={categoryOptions}
          onChange={(value) => updateFilter("categories", value as FundCategory[])}
          multiple
        />

        {/* Plan Toggle */}
        <PlanToggle
          value={filters.directOnly}
          onChange={(value) => updateFilter("directOnly", value)}
        />

        {/* Rating Filter */}
        <RatingFilter
          value={filters.ratings}
          onChange={(value) => updateFilter("ratings", value)}
        />

        <div className="flex-1" />

        {/* Sort Dropdown */}
        <SortDropdown
          field={filters.sortField}
          direction={filters.sortDirection}
          onFieldChange={(field) => updateFilter("sortField", field)}
          onDirectionChange={(dir) => updateFilter("sortDirection", dir)}
        />

        {/* Clear Filters */}
        <ClearFiltersButton filters={filters} onClear={clearFilters} />
      </div>

      {/* Active Filter Tags */}
      <ActiveFilterTags filters={filters} onRemove={removeFilter} />
    </div>
  );
}

// ============================================================================
// Skeleton Loader
// ============================================================================

/**
 * Skeleton loader for FilterBar
 */
export function FilterBarSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn("flex items-center gap-3", className)}>
      {/* Asset class chips skeleton */}
      <div className="flex gap-1">
        {[...Array(4)].map((_, i) => (
          <div
            key={i}
            className="h-8 w-16 rounded-full bg-muted animate-pulse"
          />
        ))}
      </div>

      <div className="h-6 w-px bg-border" />

      {/* Dropdown skeletons */}
      {[...Array(3)].map((_, i) => (
        <div
          key={i}
          className="h-8 w-24 rounded-md bg-muted animate-pulse"
        />
      ))}

      <div className="flex-1" />

      {/* Sort skeleton */}
      <div className="h-8 w-20 rounded-md bg-muted animate-pulse" />
    </div>
  );
}

export default FilterBar;
